/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { useCallback, FunctionComponent, useState } from 'react';
import PropTypes from 'prop-types';
import DownloadIcon from '@material-ui/icons/GetApp';
import {
  fetchRelatedRecords,
  useDataProvider,
  useNotify,
  useListContext,
  SortPayload,
  Exporter,
  FilterPayload,
  useResourceContext,
} from 'ra-core';
import Button, { ButtonProps } from './Button';
import CircularProgress from '@material-ui/core/CircularProgress';

const CustomExportButton: FunctionComponent<ExportButtonProps> = props => {
  const {
    maxResults = 1000,
    onClick,
    label = 'ra.action.export',
    icon = defaultIcon,
    exporter: customExporter,
    sort, // deprecated, to be removed in v4
    disabled,
    ...rest
  } = props;
  const {
    filter,
    filterValues,
    currentSort,
    exporter: exporterFromContext,
    total,
  } = useListContext(props);
  const resource = useResourceContext(props);
  const exporter = customExporter || exporterFromContext;
  const dataProvider = useDataProvider();
  const [isLoading, setIsLoading] = useState(false);
  const notify = useNotify();
  const handleClick = useCallback(
    event => {
      setIsLoading(true);
      dataProvider
        .getList(resource, {
          sort: currentSort || sort,
          filter: filter ? { ...filterValues, ...filter } : filterValues,
          pagination: { page: 1, perPage: maxResults },
        })
        .then(
          ({ data }) =>
            exporter &&
            exporter(
              data,
              fetchRelatedRecords(dataProvider),
              dataProvider,
              resource,
            ),
        )
        .catch(error => {
          console.error(error);
          notify('ra.notification.http_error', 'warning');
        })
        .finally(() => setIsLoading(false));
      if (typeof onClick === 'function') {
        onClick(event);
      }
    },
    [
      currentSort,
      dataProvider,
      exporter,
      filter,
      filterValues,
      maxResults,
      notify,
      onClick,
      resource,
      sort,
    ],
  );

  return (
    <Button
      onClick={handleClick}
      label={isLoading ? '' : label}
      {...sanitizeRestProps(rest)}
      disabled={disabled || total === 0 || isLoading}
    >
      {isLoading ? <CircularProgress size={16} /> : icon}
    </Button>
  );
};

const defaultIcon = <DownloadIcon />;

const sanitizeRestProps = ({
  basePath,
  filterValues,
  resource,
  ...rest
}: Omit<ExportButtonProps, 'sort' | 'maxResults' | 'label' | 'exporter'>) =>
  rest;

interface Props {
  basePath?: string;
  exporter?: Exporter;
  filterValues?: FilterPayload;
  icon?: JSX.Element;
  label?: string;
  maxResults?: number;
  onClick?: (e: Event) => void;
  resource?: string;
  sort?: SortPayload;
}

export type ExportButtonProps = Props & ButtonProps;

CustomExportButton.propTypes = {
  basePath: PropTypes.string,
  exporter: PropTypes.func,
  filterValues: PropTypes.object,
  label: PropTypes.string,
  maxResults: PropTypes.number,
  resource: PropTypes.string,
  // sort: PropTypes.exact({
  //   field: PropTypes.string,
  //   order: PropTypes.string,
  // }),
  icon: PropTypes.element,
};

export default CustomExportButton;
